import { DateTime } from 'luxon';
import devSubDomains from '../data/devSubDomains';
import { CountryType } from '../data/globalCountries';

declare const dataLayer: any, ga: any, nextStepPlugin: any, gaData: any;

export const appendScript = (scriptUrl: string, forceAppend = false) => {
  if (!forceAppend) {
    // Don't append script if already added
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; ++i) {
      if (scripts[i].src === scriptUrl) {
        return;
      }
    }
  }

  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = false;
  s.src = scriptUrl;
  const x = document.getElementsByTagName('head')[0];
  x.appendChild(s);
};

export const encodeLeadPayload = (payload: object): string =>
  btoa(JSON.stringify(payload));

export const encodeAddress = (addressString: string): string => {
  return btoa(
    encodeURIComponent(addressString).replace(
      /%([0-9A-F]{2})/g,
      function toSolidBytes(match, p1) {
        return String.fromCharCode(('0x' + p1) as any);
      }
    )
  );
};

export const decodeAddress = (str: string) => {
  if (!str) return;
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
};

export const formatPhoneNumber = (value: any) => {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, '');

  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

export const getQueryVariable = (variable: string, url = null) => {
  const query = url || window.location.search.substring(1);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]);
    }
  }
  return 'NA';
};

export const saveStep = (
  step: number,
  lpWizardId: string,
  testName: string = 'default'
) => {
  try {
    if (typeof dataLayer == 'object') dataLayer.push({ event: 'step' + step });
    if (typeof ga == 'function' && typeof ga.getAll == 'function')
      ga.getAll()[0].send('event', lpWizardId, 'step' + step);
    if (typeof nextStepPlugin == 'function') nextStepPlugin(step);
    const funnelNameParam =
      testName !== 'default' ? '&f=ab_test_' + testName : '';
    appendScript(
      'https://d2w7hu1gr02wkx.cloudfront.net/data.json?callback=loadscript&ev=15&t=' +
        step +
        '&ls=&ts=' +
        Date.now() +
        '&cid=' +
        lpWizardId +
        funnelNameParam
    );
    var gaOId = 'UA-3015252-23';
    if (
      typeof gaData != 'undefined' &&
      typeof gaData[gaOId].experiments == 'object'
    ) {
      var experimentId = Object.keys(gaData[gaOId].experiments)[0];
      var variationId = gaData[gaOId].experiments[experimentId];
      appendScript(
        'https://d2w7hu1gr02wkx.cloudfront.net/data.json?ev=17&t=' +
          lpWizardId +
          '&ls=' +
          getQueryVariable('lsid') +
          '&ts=' +
          Date.now() +
          '&cid=step' +
          step +
          '&g=' +
          encodeURIComponent(experimentId + '-' + variationId)
      );
    }
  } catch (e) {}
};

export const determineUtility = (value: number): number => {
  switch (true) {
    case value <= 99:
      return 51;
    case value >= 100 && value <= 150:
      return 125;
    case value >= 151 && value <= 200:
      return 175;
    case value >= 201 && value <= 300:
      return 250;
    case value >= 301 && value <= 400:
      return 350;
    case value >= 401 && value <= 500:
      return 450;
    case value >= 501 && value <= 600:
      return 550;
    case value >= 601 && value <= 700:
      return 650;
    case value >= 701 && value <= 800:
      return 750;
    case value >= 801:
      return 850;
    default:
      return 250;
  }
};

export const makePost = (path: string, params: any, method = 'post') => {
  const form = document.createElement('form');
  form.method = method;
  form.action = path;

  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = key;
      hiddenField.value = params[key];

      form.appendChild(hiddenField);
    }
  }

  document.body.appendChild(form);
  form.submit();
};

export const updateLeadMeta = (
  leadId: string,
  action: string,
  data: string,
  urlParams: string,
  slfApiUrl: string
) => {
  const url =
    `${slfApiUrl}/api/user/update_lead_meta/` +
    leadId +
    '/expop_' +
    action +
    '/' +
    encodeURIComponent(data) +
    (urlParams ? urlParams : '');
  appendScript(url!, true);
};

export const detectBrowser = (): string | undefined => {
  const userAgent = navigator.userAgent;

  if (userAgent.match(/chrome|chromium|crios/i)) {
    return 'chrome';
  } else if (userAgent.match(/firefox|fxios/i)) {
    return 'firefox';
  } else if (userAgent.match(/safari/i)) {
    return 'safari';
  } else if (userAgent.match(/opr\//i)) {
    return 'opera';
  } else if (userAgent.match(/edg/i)) {
    return 'edge';
  } else {
    return undefined;
  }
};

// Ref: https://stackoverflow.com/a/11381730
export const isMobile = () => {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      // eslint-disable-next-line no-useless-escape
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(4B8r3B4p7yhRXuBWLqsQ546WR43cqQwrbXMDFnBi6vSJBeif8tPW85a7r7DM961Jvk4hdryZoByEp8GC8HzsqJpRN4FxGM9)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(4B8r3B4p7yhRXuBWLqsQ546WR43cqQwrbXMDFnBi6vSJBeif8tPW85a7r7DM961Jvk4hdryZoByEp8GC8HzsqJpRN4FxGM9)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || (window as any).opera);
  return check;
};

export const getConfigFileNameForLsid = (
  lsid: string,
  testConfig: any,
  forceTest: string | null | undefined = null,
  isConsentPage: boolean = false
): any => {
  if (forceTest) {
    const test = testConfig.find((test: any) => test.name === forceTest);
    if (test) return { configFile: test.configFile, testName: test.name };
  }

  const defaultTest = { configFile: 'default', testName: 'default' };

  if (isConsentPage) return defaultTest;

  const testsForLsid = testConfig.filter(
    (test: any) =>
      ((test.lsid.length === 0 || test.lsid.includes(Number(lsid))) &&
        test.weight > 0) ||
      test.name === 'default'
  );

  if (testsForLsid.length > 1) {
    let totalWeight = testsForLsid.reduce(
      (previousValue: number, test: any) => previousValue + test.weight,
      0
    );

    if (totalWeight < 100) {
      testsForLsid.forEach((test: any) => {
        if (test.name === 'default') test.weight = 100 - totalWeight;
      });
      totalWeight = testsForLsid.reduce(
        (previousValue: number, test: any) => previousValue + test.weight,
        0
      );
    }

    const testsRanges: any[] = [];
    let initialProbability = 0,
      finalProbability;
    testsForLsid.forEach((test: any) => {
      finalProbability = initialProbability + test.weight / totalWeight;
      testsRanges.push({
        initialProbability,
        finalProbability,
        fileName: test.configFile,
        testName: test.name,
      });
      initialProbability = finalProbability;
    });
    const randomValue = Math.random();

    const currentSubDomain = window.location.hostname.split('.')[0];
    if (
      devSubDomains.some((subDomain: string) =>
        currentSubDomain.startsWith(subDomain)
      )
    ) {
      console.log('Test list matched:');
      console.log(testsRanges);
    }

    const testToUse = testsRanges.find(
      (testRange: any) =>
        randomValue >= testRange.initialProbability &&
        randomValue <= testRange.finalProbability
    );

    if (testToUse) {
      const testStates =
        testsForLsid.find((test: any) => test.name === testToUse.testName)
          ?.states || [];
      let foundState = false;
      if (testStates.length) {
        if (!window.countryData?.state) return defaultTest;
        for (const state of testStates) {
          if (state.toLowerCase() === window.countryData.state.toLowerCase()) {
            foundState = true;
            break;
          }
        }
        if (!foundState) return defaultTest;
      }
      return { configFile: testToUse.fileName, testName: testToUse.testName };
    }
  }
  return defaultTest;
};

export const appendGTM = () => {
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = false;
  const x = document.getElementsByTagName('head')[0];

  s.innerHTML = `(function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = false;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', 'GTM-W65TQC8');`;

  x.appendChild(s);

  const ns = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-W65TQC8';
  iframe.height = '0';
  iframe.width = '0';
  iframe.setAttribute('style', 'display: none; visibility: hidden');
  iframe.setAttribute('sandbox', 'allow-scripts allow-top-navigation');

  ns.appendChild(iframe);
  document.body.appendChild(ns);
};

export const appendEnphaseGTM = () => {
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = false;
  const x = document.getElementsByTagName('head')[0];

  s.innerHTML = `(function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = false;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', 'GTM-WDJV9F');`;

  x.appendChild(s);
};

export const getCountryCode = (
  languageCode: string,
  countries: CountryType[]
) => {
  const countryInfo = countries.find(
    (country) => country.languageCode === languageCode
  );
  if (countryInfo) return countryInfo.code;
  return null;
};

export const getLanguageCode = (
  countryCode: string,
  countries: CountryType[]
) => {
  const countryInfo = countries.find((country) => country.code === countryCode);
  if (countryInfo) return countryInfo.languageCode;
  return null;
};

export const formatNumber = (number: number) => {
  const formatRegex = /\B(?=(\d{3})+(?!\d))/g;
  return number
    ? `$${Math.round(number).toString().replace(formatRegex, ',')}`
    : '$0';
};

// Register time and timezone on lead metadata
const timezones = [
  {
    codes: 'EDT_EST',
    daylightName: 'Eastern Daylight Time',
    daylightOffset: -240,
    standardName: 'Eastern Standard Time',
    standardOffset: -300,
  },
  {
    codes: 'CDT_CST',
    daylightName: 'Central Daylight Time',
    daylightOffset: -300,
    standardName: 'Central Standard Time',
    standardOffset: -340,
  },
  {
    codes: 'MDT_MST',
    daylightName: 'Mountain Daylight Time',
    daylightOffset: -360,
    standardName: 'Mountain Standard Time',
    standardOffset: -420,
  },
  {
    codes: 'PDT_PST',
    daylightName: 'Pacific Daylight Time',
    daylightOffset: -420,
    standardName: 'Pacific Standard Time',
    standardOffset: -480,
  },
  {
    codes: 'AKDT_AKST',
    daylightName: 'Alaska Daylight Time',
    daylightOffset: -480,
    standardName: 'Alaska Standard Time',
    standardOffset: -540,
  },
  {
    codes: 'HST',
    standardName: 'Hawaii-Aleutian Standard Time',
    standardOffset: -600,
  },
];

export const postTimeMeta = (slfApiUrl: string, leadId: string) => {
  let timeZoneName;
  const time = DateTime.local();
  const tzCode = time.offsetNameShort;
  const isInDST = DateTime.local().isInDST;

  let timezoneMatch = timezones.find((tz) => tz.codes.includes(tzCode));
  if (!timezoneMatch) {
    const offset = time.offset;
    const offsetName = isInDST ? 'daylightOffset' : 'standardOffset';
    timezoneMatch = timezones.find((tz) => tz[offsetName] === offset);
  }
  if (timezoneMatch) {
    timeZoneName = isInDST
      ? timezoneMatch.daylightName
      : timezoneMatch.standardName;
  } else {
    timeZoneName = time.offsetNameLong;
  }

  const dateString = new Date().toString();
  const dateArray = dateString.split('(');
  const timeString = dateArray[0].trim().replace(/\s/g, '_');
  const timeZoneString = timeZoneName!.replace(/\s/g, '_');

  const timeUrl = `${slfApiUrl}/api/user/update_lead_meta/${leadId}/time/${encodeURIComponent(
    timeString
  )}`;
  appendScript(timeUrl);

  const timeZoneUrl = `${slfApiUrl}/api/user/update_lead_meta/${leadId}/timezone/${encodeURIComponent(
    timeZoneString
  )}`;
  appendScript(timeZoneUrl);
};

export const registerGoogleSolarResponse = (
  slfApiUrl: string,
  leadId: string,
  response: any
) => {
  const updateuserMetaUrl = `${slfApiUrl}/api/user/update_lead_meta/${leadId}/google_solar_api_response/${encodeURIComponent(
    JSON.stringify(response)
  )}`;
  appendScript(updateuserMetaUrl);
};
